// /**
//  * SimpleLineGraph Component
//  *
//  * Description:
//  * This component renders a simple line graph with background bands that visually represent reference and optimal ranges for a given metric.
//  * It displays a dynamic marker whose position is determined by the provided result value. The graph adjusts for various categories like 5-bar,
//  * 4-bar, 3-bar, and 2-bar configurations, handling both ascending and descending ranges with customizable colors and thresholds.
//  *
//  * The background bands and marker colors are dynamically generated based on the category provided through props, ensuring appropriate
//  * visual distinctions between thresholds such as optimal, reference, and outlier values.
//  *
//  * Key Features:
//  * - Dynamic band colors and marker positioning based on the selected category.
//  * - Handles a variety of configurations for different bar types (e.g., DND, PN).
//  * - Responsive and scalable within the provided SVG container.
//  *
//  * File Name: SimpleLineGraph.tsx
//  * Date: 19-08-2024
//  * Updated: 12-08-2025
//  */

// import React, { useRef, useEffect } from "react";
// import * as d3 from "d3";
// import {
//   barColors,
//   fiveBarMarker,
//   fourBarMarkerAsc,
//   fourBarMarkerDesc,
//   threeBarMarkerAsc,
//   threeBarMarkerCentralized,
//   threeBarMarkerDesc,
//   twoBarDNDAsc,
//   twoBarDNDDesc,
//   twoBarMarkerAsc,
//   twoBarMarkerDesc,
//   twoBarPNAsc,
//   twoBarPNDesc,
// } from "src/lib/constans";

// // Define interfaces for the component props
// interface Band {
//   x1: number;
//   x2: number;
//   color: string;
// }

// interface SimpleLineGraphProps {
//   result: number; // Position of the marker, either as a percentage (0-100) or an absolute value depending on the category.
//   refMin: number; // Reference minimum value
//   refMax: number; // Reference maximum value
//   optMin: number; // Optimal minimum value
//   optMax: number; // Optimal maximum value
//   category: string; // The category type that defines the graph configuration (e.g., 2-bar, 3-bar, 5-bar)
//   size: "md" | "lg" | "sm";
// }

// const SimpleLineGraph: React.FC<SimpleLineGraphProps> = ({
//   result,
//   refMin,
//   refMax,
//   optMin,
//   optMax,
//   category,
//   size = "md",
// }) => {
//   const svgRef = useRef<SVGSVGElement | null>(null);

//   useEffect(() => {
//     const svg = d3.select(svgRef.current);
//     const margin = { top: 20, right: 0, bottom: 20, left: 0 };
//     const width = size === "lg" ? 600 : size === "md" ? 350 : 300;
//     const height = size === "lg" ? 10 : 6;

//     let minValue;
//     let maxValue;

//     // Determine the value range based on category
//     if (
//       category === twoBarDNDAsc ||
//       category === twoBarDNDDesc ||
//       category === twoBarPNAsc ||
//       category === twoBarPNDesc
//     ) {
//       minValue = 0;
//       maxValue = 100;
//     } else {
//       minValue = result < refMin ? 0 : refMin - 10;
//       maxValue = result > refMax ? result : refMax + 10;
//     }

//     // Define band configurations for different categories

//     const fiveBarBands = [
//       { x1: minValue, x2: refMin - 1e-10, color: barColors.red },
//       { x1: refMin, x2: optMin - 1e-10, color: barColors.orange },
//       { x1: optMin, x2: optMax, color: barColors.green },
//       { x1: optMax + 1e-10, x2: refMax, color: barColors.orange },
//       { x1: refMax + 1e-10, x2: maxValue, color: barColors.red },
//     ];

//     const fourBarAscBands = [
//       { x1: minValue, x2: refMin - 1e-10, color: barColors.red },
//       { x1: refMin, x2: optMin - 1e-10, color: barColors.orange },
//       { x1: optMin, x2: optMax, color: barColors.yellow },
//       { x1: optMax + 1e-10, x2: maxValue, color: barColors.green },
//     ];

//     const fourBarDescBands = [
//       { x1: minValue, x2: optMin - 1e-10, color: barColors.green },
//       { x1: optMin, x2: optMax, color: barColors.yellow },
//       { x1: optMax + 1e-10, x2: refMax, color: barColors.orange },
//       { x1: refMax + 1e-10, x2: maxValue, color: barColors.red },
//     ];

//     const threeBarCenter = [
//       { x1: minValue, x2: optMin - 1e-10, color: barColors.red },
//       { x1: optMin, x2: optMax, color: barColors.green },
//       { x1: optMax + 1e-10, x2: maxValue, color: barColors.red },
//     ];

//     const threeBarAsc = [
//       { x1: minValue, x2: refMin, color: barColors.red },
//       { x1: refMin, x2: refMax, color: barColors.orange },
//       { x1: refMax, x2: maxValue, color: barColors.green },
//     ];

//     const threeBarDesc = [
//       { x1: minValue, x2: optMax, color: barColors.green },
//       { x1: optMax, x2: refMax, color: barColors.orange },
//       { x1: refMax, x2: maxValue, color: barColors.red },
//     ];

//     const twoBarAsc = [
//       { x1: minValue, x2: optMin - 1e-10, color: barColors.red },
//       { x1: optMin, x2: maxValue, color: barColors.green },
//     ];

//     const twoBarDesc = [
//       { x1: minValue, x2: optMax, color: barColors.green },
//       { x1: optMax, x2: maxValue, color: barColors.red },
//     ];

//     const twoBarRedGreen = [
//       { x1: 0, x2: 50, color: barColors.red },
//       { x1: 50, x2: 100, color: barColors.green },
//     ];

//     const twoBarGreenRed = [
//       { x1: 0, x2: 50, color: barColors.green },
//       { x1: 50, x2: 100, color: barColors.red },
//     ];

//     svg.selectAll("*").remove(); // Clear previous SVG content

//     const chart = svg
//       .append("g")
//       .attr("transform", `translate(${margin.left},${margin.top})`);

//     // Create an x-scale based on the value range
//     const xScale = d3
//       .scaleLinear()
//       .domain([minValue, maxValue]) // Domain based on minValue and maxValue
//       .range([0, width]); // Full width for scaling

//     // Choose the correct bands configuration based on the category
//     const bands =
//       category === fiveBarMarker
//         ? fiveBarBands
//         : category === fourBarMarkerAsc
//         ? fourBarAscBands
//         : category === fourBarMarkerDesc
//         ? fourBarDescBands
//         : category === threeBarMarkerCentralized
//         ? threeBarCenter
//         : category === threeBarMarkerAsc
//         ? threeBarAsc
//         : category === threeBarMarkerDesc
//         ? threeBarDesc
//         : category === twoBarMarkerAsc
//         ? twoBarAsc
//         : category === twoBarMarkerDesc
//         ? twoBarDesc
//         : category === twoBarDNDAsc
//         ? twoBarRedGreen
//         : category === twoBarDNDDesc
//         ? twoBarGreenRed
//         : category === twoBarPNAsc
//         ? twoBarRedGreen
//         : category === twoBarPNDesc
//         ? twoBarGreenRed
//         : [];

//     // Render the bands dynamically based on the provided configuration
//     bands.forEach((band) => {
//       chart
//         .append("rect")
//         .attr("x", xScale(band.x1)) // Set the x position
//         .attr("y", 0)
//         .attr("width", xScale(band.x2) - xScale(band.x1)) // Calculate width based on x1 and x2
//         .attr("height", height)
//         .attr("fill", band.color);
//     });

//     // Determine the circle color based on the marker's position within the bands
//     let circleColor = "#000"; // Default marker color
//     for (const band of bands) {
//       if (category === twoBarDNDAsc || category === twoBarPNAsc) {
//         circleColor = result === 1 ? barColors.green : barColors.red;
//         break;
//       }
//       if (category === twoBarDNDDesc || category === twoBarPNDesc) {
//         circleColor = result === 1 ? barColors.red : barColors.green;
//         break;
//       }
//       if (result >= band.x1 && result <= band.x2) {
//         circleColor = band.color;
//         break;
//       }
//     }

//     let _result;
//     if (
//       category === twoBarDNDAsc ||
//       category === twoBarPNAsc ||
//       category === twoBarDNDDesc ||
//       category === twoBarPNDesc
//     ) {
//       _result = result === 1 ? 75 : 25;
//     } else {
//       _result = result;
//     }

//     // Render the marker (circle) based on the computed result and color
//     chart
//       .append("circle")
//       .attr("cx", `${xScale(_result)}px`)
//       .attr("cy", height / 2)
//       .attr("r", size === "lg" ? 10 : 8)
//       .attr("fill", circleColor) // Dynamic marker color based on range
//       .attr("stroke", "white")
//       .attr("stroke-width", 2);
//   }, [result, refMin, refMax, optMin, optMax]);

//   return (
//     <div className="flex justify-center">
//       <svg
//         ref={svgRef}
//         width={size === "lg" ? 600 : size === "md" ? 350 : 300}
//         height={size === "lg" ? 80 : 50}
//       ></svg>
//     </div>
//   );
// };

// export default SimpleLineGraph;


// SimpleLineGraph.tsx
import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import {
  barColors,
  fiveBarMarker,
  fourBarMarkerAsc,
  fourBarMarkerDesc,
  threeBarMarkerAsc,
  threeBarMarkerCentralized,
  threeBarMarkerDesc,
  twoBarDNDAsc,
  twoBarDNDDesc,
  twoBarMarkerAsc,
  twoBarMarkerDesc,
  twoBarPNAsc,
  twoBarPNDesc,
} from "src/lib/constans";

interface Band { x1: number; x2: number; color: string; label?: string; }
interface SimpleLineGraphProps {
  result: number;
  refMin: number;
  refMax: number;
  optMin: number;
  optMax: number;
  category: string;
  size?: "md" | "lg" | "sm";
  showValueAbove?: boolean;
  valueFormatter?: (v:number)=>string;
}

const SimpleLineGraph: React.FC<SimpleLineGraphProps> = ({
  result,
  refMin,
  refMax,
  optMin,
  optMax,
  category,
  size = "md",
  showValueAbove = true,
  valueFormatter = (v)=> `${v}`,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Sizing fine-tuned to match the mock
    const W = size === "lg" ? 520 : size === "md" ? 320 : 280;
    const BAR_H = size === "lg" ? 12 : 8;        // slim bar
    const TOP_PAD = size === "lg" ? 26 : 22;     // room for value label
    const BOT_PAD = size === "lg" ? 24 : 22;     // room for segment labels
    const H = TOP_PAD + BAR_H + BOT_PAD;

    // Domain calc (3-bar center looks nicer when tight to thresholds)
    const isPercentCategory =
      category === twoBarDNDAsc ||
      category === twoBarDNDDesc ||
      category === twoBarPNAsc ||
      category === twoBarPNDesc;

    const domainMin = isPercentCategory ? 0 : Math.min(result, refMin) - 0.1*(refMax - refMin || 1);
    const domainMax = isPercentCategory ? 100 : Math.max(result, refMax) + 0.1*(refMax - refMin || 1);

    const x = d3.scaleLinear().domain([domainMin, domainMax]).range([0, W]);

    // Bands
    const fiveBarBands: Band[] = [
      { x1: domainMin, x2: refMin - 1e-10, color: barColors.red },
      { x1: refMin, x2: optMin - 1e-10, color: barColors.orange },
      { x1: optMin, x2: optMax, color: barColors.green, label: `${optMin}–${optMax}` },
      { x1: optMax + 1e-10, x2: refMax, color: barColors.orange },
      { x1: refMax + 1e-10, x2: domainMax, color: barColors.red },
    ];
    const fourAsc: Band[] = [
      { x1: domainMin, x2: refMin - 1e-10, color: barColors.red, label: `low <${refMin}` },
      { x1: refMin, x2: optMin - 1e-10, color: barColors.orange },
      { x1: optMin, x2: optMax, color: barColors.yellow, label: `${optMin}–${optMax}` },
      { x1: optMax + 1e-10, x2: domainMax, color: barColors.green, label: `high >${optMax}` },
    ];
    const fourDesc: Band[] = [
      { x1: domainMin, x2: optMin - 1e-10, color: barColors.green, label: `low <${optMin}` },
      { x1: optMin, x2: optMax, color: barColors.yellow, label: `${optMin}–${optMax}` },
      { x1: optMax + 1e-10, x2: refMax, color: barColors.orange },
      { x1: refMax + 1e-10, x2: domainMax, color: barColors.red, label: `high >${refMax}` },
    ];
    const threeCenter: Band[] = [
      { x1: domainMin, x2: optMin - 1e-10, color: barColors.red, label: `low <${optMin}` },
      { x1: optMin, x2: optMax, color: barColors.green, label: `${optMin}–${optMax}` },
      { x1: optMax + 1e-10, x2: domainMax, color: barColors.red, label: `high >${optMax}` },
    ];
    const threeAsc: Band[] = [
      { x1: domainMin, x2: refMin, color: barColors.red, label: `low <${refMin}` },
      { x1: refMin, x2: refMax, color: barColors.orange, label: `${refMin}–${refMax}` },
      { x1: refMax, x2: domainMax, color: barColors.green, label: `high >${refMax}` },
    ];
    const threeDesc: Band[] = [
      { x1: domainMin, x2: optMax, color: barColors.green, label: `low <${optMax}` },
      { x1: optMax, x2: refMax, color: barColors.orange },
      { x1: refMax, x2: domainMax, color: barColors.red, label: `high >${refMax}` },
    ];
    const twoAsc: Band[] = [
      { x1: domainMin, x2: optMin - 1e-10, color: barColors.red, label: `low <${optMin}` },
      { x1: optMin, x2: domainMax, color: barColors.green, label: `≥ ${optMin}` },
    ];
    const twoDesc: Band[] = [
      { x1: domainMin, x2: optMax, color: barColors.green, label: `≤ ${optMax}` },
      { x1: optMax, x2: domainMax, color: barColors.red, label: `high >${optMax}` },
    ];
    const twoRedGreen: Band[] = [{ x1: 0, x2: 50, color: barColors.red }, { x1: 50, x2: 100, color: barColors.green }];
    const twoGreenRed: Band[] = [{ x1: 0, x2: 50, color: barColors.green }, { x1: 50, x2: 100, color: barColors.red }];

    const bands: Band[] =
      category === fiveBarMarker ? fiveBarBands
      : category === fourBarMarkerAsc ? fourAsc
      : category === fourBarMarkerDesc ? fourDesc
      : category === threeBarMarkerCentralized ? threeCenter
      : category === threeBarMarkerAsc ? threeAsc
      : category === threeBarMarkerDesc ? threeDesc
      : category === twoBarMarkerAsc ? twoAsc
      : category === twoBarMarkerDesc ? twoDesc
      : category === twoBarDNDAsc ? twoRedGreen
      : category === twoBarDNDDesc ? twoGreenRed
      : category === twoBarPNAsc ? twoRedGreen
      : category === twoBarPNDesc ? twoGreenRed
      : [];

    // Main group
    const g = svg.append("g").attr("transform", `translate(0,${TOP_PAD})`);

    // BACK BASE (rounded caps) — matches mock
    g.append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", W)
      .attr("height", BAR_H)
      .attr("rx", BAR_H/2)
      .attr("fill", "#eee");

    // Colored BANDS on top (also rounded so ends look clean)
    bands.forEach((b) => {
      const x1 = x(b.x1);
      const x2 = x(b.x2);
      g.append("rect")
        .attr("x", x1)
        .attr("y", 0)
        .attr("width", Math.max(0, x2 - x1))
        .attr("height", BAR_H)
        .attr("rx", BAR_H/2)
        .attr("fill", b.color);
    });

    // Decide marker color by band the result lies in
    const clampResult = (v:number) => Math.max(domainMin, Math.min(domainMax, v));
    let circleColor = "#2b77ff"; // outer ring color (blue in mock)
    let fillColor = "#fff";      // inner white

    // Handle binary DND/PN categories
    let markerValue = result;
    if (
      category === twoBarDNDAsc || category === twoBarPNAsc ||
      category === twoBarDNDDesc || category === twoBarPNDesc
    ) {
      // Place at 25% or 75% like your original, but still render double ring
      markerValue = result === 1 ? (category === twoBarDNDDesc || category === twoBarPNDesc ? domainMin + 0.75*(domainMax-domainMin) : domainMin + 0.75*(domainMax-domainMin))
                                 : domainMin + 0.25*(domainMax-domainMin);
    }

    // If not binary, color the inner dot to match current band
    for (const b of bands) {
      if (markerValue >= b.x1 && markerValue <= b.x2) {
        fillColor = b.color === barColors.red || b.color === barColors.orange ? "#fff" : "#fff";
        break;
      }
    }

    const cx = x(clampResult(markerValue));
    const cy = BAR_H/2;

    // Marker (double ring like mock: white dot + blue stroke + white inner stroke)
    g.append("circle")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("r", size === "lg" ? 8 : 6)
      .attr("fill", "#fff")
      .attr("stroke", circleColor)
      .attr("stroke-width", 3);

    g.append("circle")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("r", size === "lg" ? 4.5 : 3.5)
      .attr("fill", "#fff");

    // Value above marker
    if (showValueAbove) {
      svg.append("text")
        .attr("x", cx)
        .attr("y", TOP_PAD - 8)
        .attr("text-anchor", "middle")
        .attr("font-size", size === "lg" ? 16 : 13)
        .attr("fill", "#4b5563")
        .text(valueFormatter(result));
    }

    // Segment labels below — only for 3-bar center & 4/5 bar types
    const labelY = TOP_PAD + BAR_H + (size === "lg" ? 18 : 16);
    if (bands.length >= 3) {
      // left label (low)
      const leftLabel = bands[0].label ?? "";
      const centerLabel = bands[Math.floor(bands.length/2)].label ?? "";
      const rightLabel = bands[bands.length-1].label ?? "";

      svg.append("text")
        .attr("x", 4) // left align
        .attr("y", labelY)
        .attr("text-anchor", "start")
        .attr("font-size", size === "lg" ? 12 : 11)
        .attr("fill", "#6b7280")
        .text(leftLabel);

      svg.append("text")
        .attr("x", W/2)
        .attr("y", labelY)
        .attr("text-anchor", "middle")
        .attr("font-size", size === "lg" ? 12 : 11)
        .attr("fill", "#6b7280")
        .text(centerLabel);

      svg.append("text")
        .attr("x", W - 4)
        .attr("y", labelY)
        .attr("text-anchor", "end")
        .attr("font-size", size === "lg" ? 12 : 11)
        .attr("fill", "#6b7280")
        .text(rightLabel);
    }
  }, [result, refMin, refMax, optMin, optMax, category, size, showValueAbove, valueFormatter]);

  // SVG height auto from calc
  const height = (size === "lg" ? 26 : 22) + (size === "lg" ? 12 : 8) + (size === "lg" ? 24 : 22);

  return (
    <div className="flex justify-center">
      <svg ref={svgRef} width={size === "lg" ? 520 : size === "md" ? 320 : 280} height={height} />
    </div>
  );
};

export default SimpleLineGraph;
